/* eslint-disable react/no-array-index-key */
import {
  Fragment,
  useCallback, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import STRING_KEYS from '../../../../../language/keys';
import { ExplainabilityRelevantSkillSourceEnum } from '../../../../../enums/match/explainability-relevant-skill-source.enum';
import { SeeMoreButton } from '../timeline/see-more-button/see-more-button.component';
import { ExplainabilitySkill } from '../../../../../store/rtk-query/apis/match/dto/response/match.response';
import { TooltipBubble } from '../../../../../components/Common/TooltipBubble';
import { THEME } from '../../../../../themes';
import BaseWidget from '../widgets/base-widget/base-widget.component';
import {
  AskedByUserChip,
  CheckedStarIcon,
  ChipsContainer,
  ChipsContainerWrapper,
  AdditionalSkillChip,
  SKILLS_CHIP_GAP_PX,
  TempleIcon,
  ContainerIcon,
  VSignIcon,
} from './skills-container.styled';
import { SkillsContainerProps } from './skills-container';

const CHIP_HEIGHT_PX = 32;
const MAX_MINIMIZED_CHIP_ROWS = 3;

const dedupSkills = (relevantSkills: ExplainabilitySkill[], additionalSkills: ExplainabilitySkill[]): ExplainabilitySkill[] => {
  if (!additionalSkills?.length || !relevantSkills?.length) {
    return additionalSkills;
  }

  const lowerCasedRelevantSkills = new Set(relevantSkills.map((skill) => skill.value.toLowerCase()));

  return additionalSkills.filter((skill) => !lowerCasedRelevantSkills.has(skill.value.toLowerCase()));
};

const SkillsContainer = ({ explainability, matchId }: SkillsContainerProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const [[minimizedHeight, fullHeight], setHeights] = useState<[number, number]>([-1, -1]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const hasMoreChipsToShow = useMemo(() => minimizedHeight !== -1, [minimizedHeight]);

  const measuredRef = useCallback((node: HTMLDivElement) => {
    if (!node) {
      return null;
    }

    const maxMinimizedSize = (CHIP_HEIGHT_PX * MAX_MINIMIZED_CHIP_ROWS) + ((MAX_MINIMIZED_CHIP_ROWS - 1) * SKILLS_CHIP_GAP_PX);

    if (node.clientHeight > maxMinimizedSize) {
      setHeights([maxMinimizedSize, node.clientHeight]);
    }

    return node;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explainability]);

  const renderAdditionalSkillChip = (chipText: string, tooltipText?: string) => {
    const chip = (
      <AdditionalSkillChip
        StartAdornment={ <VSignIcon /> }
      >
        {chipText}
      </AdditionalSkillChip>
    );

    if (tooltipText) {
      return (
        <TooltipBubble title={ tooltipText }>
          { chip }
        </TooltipBubble>
      );
    }

    return chip;
  };

  const renderAskedByUserChip = (chipText: string, Icon: JSX.Element, tooltipText?: string) => {
    const chip = (
      <AskedByUserChip
        StartAdornment={ Icon }
      >
        {chipText}
      </AskedByUserChip>
    );

    if (tooltipText) {
      return (
        <TooltipBubble title={ tooltipText }>
          { chip }
        </TooltipBubble>
      );
    }

    return chip;
  };

  const renderCompanyInsightSkills = () => {
    return explainability?.companyInsights?.map((skill, index) => {
      return (
        <Fragment
          key={ `${matchId}-company-insight-skills-${skill.value}-${index}` }
        >
          { renderAskedByUserChip(skill.value, <TempleIcon />, translate(STRING_KEYS.MATCH_CARD.SKILLS.COMPANY_INSIGHT_TOOLTIP_TEXT)) }
        </Fragment>
      );
    });
  };

  const renderTalentRelevantSkills = () => {
    return explainability?.relevantSkills?.map((skill, index) => {
      const tooltipText = skill.source === ExplainabilityRelevantSkillSourceEnum.Inferred ?
        translate(STRING_KEYS.MATCH_CARD.SKILLS.INFERRED_SOURCE_TOOLTIP_TEXT) : translate(STRING_KEYS.MATCH_CARD.SKILLS.TALENT_PROFILE_SOURCE_TOOLTIP_TEXT);

      return (
        <Fragment
          key={ `${matchId}-relevant-skills-${skill.value}-${index}` }
        >
          { renderAskedByUserChip(skill.value, <CheckedStarIcon />, tooltipText) }
        </Fragment>
      );
    });
  };

  const renderAdditionalSkills = (additionalSkills: ExplainabilitySkill[]) => {
    return additionalSkills?.map((skill, index) => {
      return (
        <Fragment
          key={ `${matchId}-additional-skills-${skill.value}-${index}` }
        >
          { renderAdditionalSkillChip(skill.value, translate(STRING_KEYS.MATCH_CARD.SKILLS.TALENT_PROFILE_SOURCE_TOOLTIP_TEXT)) }
        </Fragment>
      );
    });
  };

  const renderChips = () => {
    return (
      <>
        { renderTalentRelevantSkills() }
        { renderCompanyInsightSkills() }
        { renderAdditionalSkills(dedupSkills(explainability?.relevantSkills, explainability?.additionalSkills)) }
      </>
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const Chips = useMemo(renderChips, [explainability]);

  return (
    <BaseWidget
      title={ translate(STRING_KEYS.MATCH_WIDGETS.SKILLS.TITLE) }
      Icon={ ContainerIcon }
      titleColor={ THEME.COLORS.PRIMARY_DARK }
    >
      <div>
        <ChipsContainerWrapper isExpended={ isExpanded } fullHeight={ fullHeight } minimizedHeight={ minimizedHeight } >
          <ChipsContainer ref={ measuredRef }>
            { Chips }
          </ChipsContainer>
        </ChipsContainerWrapper>
        { hasMoreChipsToShow && !isExpanded && <SeeMoreButton clickHandler={ () => setIsExpanded(true) } /> }
      </div>
    </BaseWidget>
  );
};

export default SkillsContainer;
